import 'reactflow/dist/style.css';
import './ReactFlowTree.scss';
import isEqual from 'lodash/isEqual';
import { useEffect, useState, useCallback, useMemo } from 'react';
import ReactFlow, {
	MiniMap,
	ReactFlowProvider,
	useUpdateNodeInternals,
	OnViewportChange,
	PanOnScrollMode,
	Panel,
	PanelPosition,
	useViewport,
	NodeChange,
	EdgeChange,
	getConnectedEdges,
} from 'reactflow';
import { Key } from 'ts-key-enum';

import { EDGE_TYPE_COMPONENT_MAP } from './EdgeTypes';
import { FlowTreePanel } from './FlowTreePanel';
import { NODE_TYPE_COMPONENT_MAP } from './NodeTypes';
import { useReactFlow, applyNodeChanges, applyEdgeChanges } from './functions';
import { useLayout } from './hooks/useLayout';
import { ReactFlowEdge, ReactFlowNode } from './types';

const MAX_ZOOM = 1;
const MIN_ZOOM = 0.1;
const fitViewOptions = { padding: 0.95 };
const proOptions = { account: 'paid-pro', hideAttribution: true };
const INITIAL_POSITION = { x: 0, y: 0 };

export const ZOOM_DISABLE_ACTIVATION_KEY_CODE = 'EMPTY';

interface FlowProps {
	nodes: ReactFlowNode[];
	edges: ReactFlowEdge[];
	nodeToFocus: ReactFlowNode['id'];
	onViewPortChange: OnViewportChange;
	clusterSizeMap: Record<string, Record<string, number>>;
	isNarrowBranchesLayout: boolean;
	isInterfaceFocusedMode: boolean;
	panOnScroll: boolean;
	panOnDrag: boolean;
	runFeedbackFlow: () => void;
	onRecenter: () => void;
	controlsPosition: PanelPosition | 'none';
	isWideWidget: boolean;
	showMiniMap: boolean;
	zoomOnScroll: boolean;
	zoomActivationKeyCode?: Key | Key[] | typeof ZOOM_DISABLE_ACTIVATION_KEY_CODE;
}

function Flow({
	nodes,
	edges,
	nodeToFocus,
	controlsPosition,
	clusterSizeMap,
	isNarrowBranchesLayout,
	isInterfaceFocusedMode,
	panOnScroll = false,
	panOnDrag = true,
	runFeedbackFlow,
	onRecenter,
	showMiniMap,
	zoomOnScroll = true,
	zoomActivationKeyCode,
}: FlowProps) {
	const [edgesToShow, setEdges] = useState<ReactFlowEdge[]>(edges);
	const [nodesToShow, setNodes] = useState<ReactFlowNode[]>(
		nodes.map((node) => ({ ...node, position: INITIAL_POSITION, style: { opacity: 0 } }))
	);
	// const [zoom, setZoom] = useState(useViewport().zoom);

	const [clusterSizesMap, setClusterSizesMap] = useState(clusterSizeMap);
	useEffect(() => {
		if (!isEqual(clusterSizeMap, clusterSizesMap)) {
			setClusterSizesMap(clusterSizeMap);
		}
	}, [clusterSizeMap]);

	const { fitView } = useReactFlow();
	const updateNodeInternals = useUpdateNodeInternals();
	const { zoom } = useViewport();

	const focusOnNode = (nodeToFocusOn?: ReactFlowNode) => {
		fitView({
			duration: 100,
			...fitViewOptions,
			minZoom: zoom,
			maxZoom: zoom,
			...(nodeToFocusOn ? { nodes: [nodeToFocusOn] } : {}),
		});
	};
	useEffect(() => {
		const nodeToFocusOn = nodesToShow.find(({ id }) => id == nodeToFocus);
		// in case nodeToFocusOn is missing, it will fit the view to all nodes
		focusOnNode(nodeToFocusOn);
	}, [nodeToFocus, fitView]);

	useEffect(() => {
		const updatedNodes = nodes.map((node) => {
			const { data, type } = node;
			const currentNode = nodesToShow.find(({ id }) => node.id == id);

			const newNode = {
				...node,
				...currentNode,
				position: currentNode?.position || INITIAL_POSITION,
				type,
				data, // Use the new data, but keep currentNode which contains stuff from useLayout
			};
			return newNode;
		});
		setNodes([...updatedNodes]);
		if (!isEqual(edges, edgesToShow)) {
			const newEdges = edges.map((edge) => {
				const currentEdge = edgesToShow.find(({ id }) => edge.id == id);
				return {
					...edge,
					style: currentEdge?.style || { opacity: 0 }, // Use the current opacity if it was already set
				};
			});
			setEdges([...newEdges]);
		}
		setTimeout(() => {
			updateNodeInternals(nodes.map(({ id }) => String(id)));
		});
	}, [nodes, edges, updateNodeInternals]);

	// We must updates Nodes/Edges after events, else we will get infinite loop since v11.10.4
	// See:
	// https://github.com/xyflow/xyflow/pull/3897
	const onNodesChange = useCallback((changes: NodeChange[]) => setNodes((nds) => applyNodeChanges(changes, nds)), []);
	const onEdgesChange = useCallback((changes: EdgeChange[]) => setEdges((eds) => applyEdgeChanges(changes, eds)), []);

	/**
	 * we provide useLayout with the setNodes and setEdges functions so it can update the nodes and edges,
	 * we're not using useReactFlow().setNodes/setEdges because it's not working well with controlled flow
	 * more details here:
	 * https://github.com/xyflow/xyflow/issues/3653#issuecomment-1893637513
	 *
	 * we also call it after watching the nodes and edges props state, so it can layout the graph when all data is ready
	 */
	useLayout(clusterSizesMap, isNarrowBranchesLayout, isInterfaceFocusedMode);

	const className = useMemo(() => {
		const classes = [];
		if (!panOnDrag) {
			classes.push('normal-cursor');
		}
		return classes.join(' ');
	}, [panOnDrag]);

	const setEdgeActive = (edge: ReactFlowEdge, isActive: boolean): ReactFlowEdge => ({ ...edge, data: { ...edge.data, isActive } });

	const onEdgeMouseEnter = useCallback(
		(_, edge: ReactFlowEdge) => {
			setEdges((eds) =>
				eds.map((e) => {
					if (e.id === edge.id) {
						return setEdgeActive(e, true);
					}
					return e;
				})
			);
		},
		[edgesToShow, setEdges]
	);
	const onEdgeMouseLeave = useCallback(
		(_, edge: ReactFlowEdge) => {
			setEdges((eds) =>
				eds.map((e) => {
					if (e.id === edge.id) {
						return setEdgeActive(e, false);
					}
					return e;
				})
			);
		},
		[edgesToShow, setEdges]
	);

	const onNodeMouseEnter = useCallback(
		(_, node: ReactFlowNode) => {
			const connectedEdgesIds = getConnectedEdges([node], edgesToShow).map((e) => e.id);

			setEdges((eds) =>
				eds.map((e) => {
					if (connectedEdgesIds.includes(e.id)) {
						return setEdgeActive(e, true);
					}
					return e;
				})
			);
		},
		[edgesToShow, setEdges]
	);

	const onNodeMouseLeave = useCallback(
		(_, node: ReactFlowNode) => {
			const connectedEdgesIds = getConnectedEdges([node], edgesToShow).map((e) => e.id);
			setEdges((eds) =>
				eds.map((e) => {
					if (connectedEdgesIds.includes(e.id)) {
						return setEdgeActive(e, false);
					}
					return e;
				})
			);
		},
		[edgesToShow, setEdges]
	);

	return (
		<div style={{ width: '100%', height: '100%' }}>
			<ReactFlow
				maxZoom={MAX_ZOOM}
				minZoom={MIN_ZOOM}
				selectionKeyCode=""
				nodes={nodesToShow}
				edges={edgesToShow}
				deleteKeyCode={null}
				nodesDraggable={false}
				proOptions={proOptions}
				nodesConnectable={false}
				zoomOnDoubleClick={false}
				edgesFocusable={false}
				fitViewOptions={fitViewOptions}
				edgeTypes={EDGE_TYPE_COMPONENT_MAP}
				nodeTypes={NODE_TYPE_COMPONENT_MAP}
				onNodesChange={onNodesChange}
				onEdgesChange={onEdgesChange}
				panOnScroll={panOnScroll}
				panOnScrollMode={PanOnScrollMode.Vertical}
				panOnDrag={panOnDrag}
				zoomOnScroll={zoomOnScroll}
				{...(zoomActivationKeyCode
					? { zoomActivationKeyCode: zoomActivationKeyCode === ZOOM_DISABLE_ACTIVATION_KEY_CODE ? null : zoomActivationKeyCode }
					: {})}
				onNodeMouseEnter={onNodeMouseEnter}
				onNodeMouseLeave={onNodeMouseLeave}
				onEdgeMouseEnter={onEdgeMouseEnter}
				onEdgeMouseLeave={onEdgeMouseLeave}
				elementsSelectable={false}
				className={className}
			>
				{showMiniMap && <MiniMap position="bottom-right" pannable={true} />}
				{controlsPosition !== 'none' && (
					<Panel position={controlsPosition}>
						<FlowTreePanel runFeedbackFlow={runFeedbackFlow} recenter={onRecenter} />
					</Panel>
				)}
			</ReactFlow>
		</div>
	);
}

// wrapping with ReactFlowProvider is done outside of the component
function FlowWithProvider(props: FlowProps) {
	return (
		<ReactFlowProvider>
			<Flow {...props} />
		</ReactFlowProvider>
	);
}

export default FlowWithProvider;
